import { ColDef, ColGroupDef } from "ag-grid-community";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import DeliverableService from "../../../../features/DeliverableView/services/DeliverableService";
import CoaService from "../../../../features/CoaView/services/CoaService";
import TemplateService from "../../../../features/TemplateView/services/TemplateService";
import { TemplateJsonResponse } from "../AgGridUtils";
import { transformRowData } from "./useColumnDefs";

interface UseAgGridFetchParams {
  deliverableId: string | undefined;
  sheetId: string | undefined;
  pathname: string;
  isSelfFetching: boolean;
  deliverableService: DeliverableService;
  coaService: CoaService;
  templateService: TemplateService;
  isEditMode: boolean;
  transformColumnDefs: (
    rawDefs: any[],
    mainColumnDefs?: Record<string, string[]>,
  ) => (ColDef | ColGroupDef)[];
}

export const useAgGridFetch = ({
  deliverableId,
  sheetId,
  pathname,
  isSelfFetching,
  deliverableService,
  coaService,
  templateService,
  isEditMode,
  transformColumnDefs,
}: UseAgGridFetchParams) => {
  const [internalRowData, setInternalRowData] = useState<any[]>([]);
  const [internalColumnDefs, setInternalColumnDefs] = useState<ColDef[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const rawColumnDefsRef = useRef<any[]>([]);
  const mainColumnDefsRef = useRef<Record<string, string[]>>({});

  useEffect(() => {
    if (rawColumnDefsRef.current.length > 0) {
      setInternalColumnDefs(
        transformColumnDefs(
          rawColumnDefsRef.current,
          mainColumnDefsRef.current,
        ) as ColDef[],
      );
    }
  }, [isEditMode, transformColumnDefs]);

  const fetchData = useCallback(async () => {
    if (!isSelfFetching) return;
    setInternalLoading(true);
    try {
      const numericDeliverableId = Number(deliverableId);
      const numericSheetId = Number(sheetId);
      let response:
        | TemplateJsonResponse
        | AxiosResponse<ArrayBuffer | Blob>
        | ArrayBuffer
        | Blob
        | undefined;

      if (pathname.includes("/deliverable") && pathname.includes("template")) {
        response = await deliverableService.getDeliverableTemplateUsingAxios(
          numericDeliverableId,
          true,
        );
      } else if (pathname.includes("/deliverable")) {
        response = await deliverableService.getTemplateUsingAxiosWithJson(
          numericDeliverableId,
          numericSheetId,
          false,
          0,
          100,
          undefined,
          true,
        );
      } else if (pathname.includes("/coa")) {
        response = await coaService.getCoaTemplateUsingAxios(numericDeliverableId);
      } else if (pathname.includes("/template")) {
        response = await templateService.getRuleEngineTemplateUsingAxios(
          numericDeliverableId,
          true,
        );
      }

      if (response && "jsonString" in response && response?.jsonString) {
        const parsed = JSON.parse(response.jsonString);
        const gridData = parsed[0];
        if (gridData?.columnDefs) {
          rawColumnDefsRef.current = gridData.columnDefs;
          mainColumnDefsRef.current = gridData.mainColumnDefs ?? {};
          setInternalColumnDefs(
            transformColumnDefs(
              gridData.columnDefs,
              mainColumnDefsRef.current,
            ) as ColDef[],
          );
        }
        if (gridData?.rowData) {
          setInternalRowData(transformRowData(gridData.rowData));
        }
      }
    } catch (error) {
      console.error("AG Grid fetchData error:", error);
    } finally {
      setInternalLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliverableId,
    sheetId,
    pathname,
    isSelfFetching,
    deliverableService,
    transformColumnDefs,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { internalRowData, internalColumnDefs, internalLoading, fetchData };
};
